import { useFormik } from "formik";
import { useMutation, useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import {
  generateUserValidationSchema,
  generatePlatformUserValidationSchema,
} from "validations/users";

import StatusType from "enums/StatusType";
import {
  GET_MY_PROFILE,
  GET_USER_BY_ID,
  REACTIVATE_USER,
  REMOVE_USER,
  RESEND_INVITE_EMAIL,
  RESET_USER_PASSWORD,
  SEND_PERSONAL_BANK_ACCOUNT_EMAIL,
  UPDATE_MY_PROFILE,
  UPDATE_USER,
} from "graphql/user";
import { useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import { GET_CARDS } from "graphql/card";
import { cardRowData } from "utils/cards/cardRowData";
import type { CardRowNode } from "utils/cards/cardRowData";
import useCustomFieldRenderer from "hooks/useCustomFieldRenderer";
import CustomFieldParentType from "enums/CustomFieldParentType";
import { CustomFieldsRecord } from "hooks/customFields.types";
import dayjs from "dayjs";
import { useUserMentions } from "context/UserMentionProvider";

export type FormikAddUser = {
  companyId?: string;
  profilePicture?: string;
  image?: string;
  id?: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  employeeGroup: string;
  status: string;
  address: {
    addressName: string;
    address1: string;
    address2: string;
    city: string;
    state: string;
    zipcode: string;
    country: string;
  };
  location: {
    id: string;
    addressName: string;
  };
  assignedManager: {
    id: string;
    firstName: string;
    lastName?: string | null;
    email: string;
  };
  userPassword: string;
  role: string;
  hasPhysicalCard?: boolean;
  sendOneTimePassword?: boolean;
  createAndAssignNewCard?: boolean;
  profilePicKey?: string;
  imageKey?: string;
  birthdate: Date | null;
  customFields?: CustomFieldsRecord;
  company?: {
    id: string;
    businessLegalName: string;
  };
};

type useUserProfileDataProps = {
  selectedUserId?: string;
  loadCards?: boolean;
  reactivateUserAfterEdit?: boolean;
  handleUpdateUser?: (error?: string) => void;
  setIsEditable?: (isEditable: boolean) => void;
  setReactivateUser?: (reactivate: boolean) => void;
  isPlatform?: boolean;
  /** The viewer's own record, saved through updateMyProfile: updateUser is Admin and above. */
  self?: boolean;
};

const useUserProfileData = ({
  selectedUserId,
  loadCards = false,
  reactivateUserAfterEdit = false,
  handleUpdateUser = () => {},
  setIsEditable = () => {},
  setReactivateUser = () => {},
  isPlatform = false,
  self = false,
}: useUserProfileDataProps) => {
  const { t } = useTranslation();
  const { refetch: refetchGlobalUsers } = useUserMentions();

  const {
    customFieldSettings,
    transformCustomFieldsToRecord,
    transformCustomFieldsToArray,
  } = useCustomFieldRenderer({
    parentType: CustomFieldParentType.USER,
  });
  const validationSchema = isPlatform
    ? generatePlatformUserValidationSchema(customFieldSettings)
    : generateUserValidationSchema(customFieldSettings);

  const saveCallbacks = {
    onCompleted: async () => {
      formikAddUser.resetForm({ values: formikAddUser.values });
      refetchGlobalUsers();
      handleUpdateUser();
      await refetchGetUserById();
      setIsEditable(false);
      !reactivateUserAfterEdit &&
        toast.success(t("User updated successfully!"));
    },
    onError: (e: Error) => {
      handleUpdateUser(e.message);
      toast.error(t("Failed to update user") + ": " + e.message);
    },
  };
  const [updateUser, { loading: isUpdatingUser }] = useMutation(
    UPDATE_USER,
    saveCallbacks,
  );
  const [updateMyProfile, { loading: isUpdatingMyProfile }] = useMutation(
    UPDATE_MY_PROFILE,
    saveCallbacks,
  );
  const isUpdateUser = isUpdatingUser || isUpdatingMyProfile;

  const {
    data: userData,
    loading: loadingUsers,
    refetch: refetchGetUserById,
  } = useQuery(self ? GET_MY_PROFILE : GET_USER_BY_ID, {
    variables: self ? {} : { id: selectedUserId },
    skip: !selectedUserId,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "cache-and-network",
  });
  const profile = self ? userData?.user : userData?.userById;

  const {
    data: cardsData,
    loading: loadingCards,
    refetch: refetchCardData,
  } = useQuery(GET_CARDS, {
    variables: {
      first: 5,
      companyId: userData?.companyId,
      userId: selectedUserId,
    },
    skip: !selectedUserId || !loadCards,
    fetchPolicy: "network-only",
  });

  const cards = useMemo(
    () =>
      (cardsData?.cards?.edges ?? []).map((edge: { node: CardRowNode }) =>
        cardRowData(edge.node),
      ),
    [cardsData],
  );

  const formikAddUser = useFormik<FormikAddUser>({
    initialValues: {
      id: "",
      firstName: "",
      lastName: "",
      email: "",
      employeeGroup: "",
      phone: "",
      status: StatusType.ACTIVE,
      hasPhysicalCard: false,
      address: {
        addressName: "",
        address1: "",
        address2: "",
        city: "",
        state: "",
        zipcode: "",
        country: "US",
      },
      location: {
        id: "",
        addressName: "",
      },
      assignedManager: {
        id: "",
        firstName: "",
        lastName: "",
        email: "",
      },
      userPassword: "",
      role: "Admin",
      birthdate: null,
      customFields: {},
      company: {
        id: "",
        businessLegalName: "",
      },
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      const {
        id,
        address,
        email,
        firstName,
        lastName,
        phone,
        role,
        assignedManager,
        birthdate,
        customFields,
        companyId,
        location,
      } = values;

      if (self) {
        await updateMyProfile({
          variables: {
            input: {
              firstName,
              lastName,
              phone,
              address: {
                addressName: address.addressName,
                address1: address.address1,
                address2: address.address2,
                city: address.city,
                zipcode: address.zipcode.toString(),
                country: "US",
                state: address.state,
              },
              birthdate: dayjs(birthdate).valueOf(),
              location: { id: location.id },
              customFields: transformCustomFieldsToArray(customFields),
            },
          },
        });
        return;
      }

      const baseInput = {
        userId: profile?.userId,
        companyId: companyId || profile?.company?.id,
        departmentId: profile?.department?.id,
        firstName: firstName,
        middleName: "",
        lastName: lastName,
        title: "Other",
        role: role,
        email: email,
        customFields: transformCustomFieldsToArray(customFields),
      };

      const fullInput = {
        ...baseInput,
        phone: phone,
        managerId: assignedManager.id,
        address: {
          addressName: address.addressName,
          address1: address.address1,
          address2: address.address2,
          city: address.city,
          zipcode: address.zipcode.toString(),
          country: "US",
          state: address.state,
        },
        birthdate: dayjs(birthdate).valueOf(),
        location: {
          id: location.id,
        },
      };

      await updateUser({
        variables: {
          id: id,
          input: isPlatform ? baseInput : fullInput,
        },
      });

      if (reactivateUserAfterEdit && id) {
        await handleReactivateUser(id);
      }
    },
  });

  useEffect(() => {
    if (profile?.id) {
      const initialValuesAddUser = {
        companyId: profile?.company?.id,
        id: profile?.id || "",
        firstName: profile?.firstName || "",
        lastName: profile?.lastName || "",
        email: profile?.email || "",
        phone: profile?.phone || "",
        role: profile?.role || "",
        address: profile?.address || "",
        location: {
          id: profile?.location?.id || "",
          addressName: profile?.location?.addressName || "",
        },
        assignedManager: {
          id: profile?.manager?.id || "",
          firstName: profile?.manager?.firstName || "",
          lastName: profile?.manager?.lastName || "",
          email: profile?.manager?.email || "",
        },
        employeeGroup: "",
        status: profile?.status,
        userPassword: "",
        hasPhysicalCard: profile?.hasPhysicalCard || false,
        birthdate: profile?.birthdate || null,
        image: profile?.profilePicUrl || "",
        profilePicKey: profile?.profilePicKey || "",
        company: profile?.company,
        customFields: transformCustomFieldsToRecord(
          profile?.customFields,
          customFieldSettings,
        ),
      };

      formikAddUser.resetForm({ values: initialValuesAddUser });
    } else {
      formikAddUser.resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  const [resetUserPasswordQuery, { loading: resettingUserPassword }] =
    useMutation(RESET_USER_PASSWORD);

  const resetUserPassword = async (email: string) => {
    try {
      await resetUserPasswordQuery({ variables: { email } });
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const [removeUserQuery, { loading: removingUser }] = useMutation(
    REMOVE_USER,
    {},
  );

  const handleRemoveUser = async (id: string) => {
    try {
      await removeUserQuery({ variables: { id } });
      refetchGlobalUsers();
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const [resendUserInviteEmail, { loading: resendingUserInvite }] =
    useMutation(RESEND_INVITE_EMAIL);

  const handleResendUserInviteEmail = async (email: string) => {
    try {
      await resendUserInviteEmail({ variables: { email } });
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const [reactivateUser, { loading: reactivatingUser }] =
    useMutation(REACTIVATE_USER);

  const handleReactivateUser = async (id: string) => {
    try {
      await reactivateUser({ variables: { id } });
      toast.success(t("User reactivated successfully!"));
      setReactivateUser(false);
      handleUpdateUser();
    } catch (err) {
      console.error(err);
      toast.error(t("Failed to reactivate user"));
    }
  };

  const [sendBankAccountInvite, { loading: sendingBankAccountInvite }] =
    useMutation(SEND_PERSONAL_BANK_ACCOUNT_EMAIL);

  const handleSendBankAccountInvite = async (id: string) => {
    try {
      await sendBankAccountInvite({ variables: { id } });
    } catch (err) {
      console.error(err);
    }
  };

  const specificLoadingStates = {
    loadingUsers,
    loadingCards,
    isUpdateUser,
    resettingUserPassword,
    removingUser,
    resendingUserInvite,
    reactivatingUser,
    sendingBankAccountInvite,
  };

  return {
    formikAddUser,
    customFieldSettings,
    cards,
    specificLoadingStates,
    refetchCardData,
    resetUserPassword,
    handleRemoveUser,
    handleResendUserInviteEmail,
    handleReactivateUser,
    handleSendBankAccountInvite,
  };
};

export default useUserProfileData;
